import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';

import 'react-bootstrap-typeahead/css/Typeahead.css';
import './styles.css';
import { FilterOperatorTypes } from 'util/table-view/TableTypes';
import axios from 'axios';
import Highlighter from 'react-highlight-words';
import { TextField } from '@material-ui/core';
import clsx from 'clsx';

const PER_PAGE = 3;

async function makeAndHandleRequest(uri, query, lookupQuery, fieldNameForFilter, page = 0, returnField = null, returnAllFields = false, globalSearch = false, perPage = PER_PAGE) {
  
  if(!returnField){
    returnField = fieldNameForFilter;
  }

    const resp = await axios.post(uri, {
    ...lookupQuery,
    q: query?.trim() ?? '', 
    filters: [
      ...lookupQuery.filters,
      ...(!globalSearch?[{ operator: FilterOperatorTypes.contains, value: [query?.trim() ?? ''], field: fieldNameForFilter }]:[])
    ],
    page: page,
    size: perPage,
    expFields: lookupQuery.filters.map((f)=>{return f.field}).concat([fieldNameForFilter]).concat(fieldNameForFilter !== returnField ? [returnField]:[]),
    uniqueCheckBy: fieldNameForFilter,
  });
  const { results, total } = resp.data;
  const options = results.map((i, index) => {
    if(returnAllFields){
      return {
        id: i.id ?? index,
        ...i
      }
    }
    else{
      return {
        id: i.id ?? index,
        [returnField]: i[returnField] 
      }
    }
  });
  return { options, total };
}

export function AsyncAutocomplete(props) {
  let perPage = props.perPage || PER_PAGE;
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState('');
  const [lookupQuery, setLookupQuery] = useState(props.lookupQuery);
  const cacheRef = useRef({}); // Create a local cache for each instance

  useEffect(() => {
    if(props?.initSearch){
      handleSearch();
    }
  }, []);

  useEffect(() => {
    if(props.lookupQuery){
      setLookupQuery(props.lookupQuery);
    }
    cacheRef.current = {};
    setOptions([]);
    handleInputChange('');
  }, [props.lookupQuery]);

  const handleInputChange = (q) => {
      setQuery(q.trim());
      if(props.onInputChange){
        props.onInputChange(q);
      }
  };

  const handlePagination = (e, shownResults) => {
    const cachedQuery = cacheRef.current[query?query:"query"];

    // Don't make another request if:
    // - the cached results exceed the shown results
    // - we've already fetched all possible results
    if (
      cachedQuery &&
      cachedQuery.options.length > shownResults &&
      cachedQuery.options.length === cachedQuery.total
    ) {
      return;
    }

    setIsLoading(true);

    const page = (cachedQuery && cachedQuery.page + 1) || 1; // Initialize page to 1 if not in cache

    makeAndHandleRequest(props.searchUri, query, lookupQuery, props.fieldNameForFilter, page, props.returnField, props.returnAllFields, props.globalSearch, perPage).then((resp) => {
        const newOptions = (cachedQuery ? cachedQuery.options : []).concat(resp.options);
        const newCache = {
          ...cacheRef.current,
          [query?query:"query"]: { ...cachedQuery, options: newOptions, page },
        };

        cacheRef.current = newCache;
        setIsLoading(false);
        setOptions(newOptions);
      }
    );
  };

  // `handleInputChange` updates state and triggers a re-render, so
  // use `useCallback` to prevent the debounced search handler from
  // being cancelled.
  const handleSearch = useCallback((q) => {
    if (cacheRef.current[q?q:"query"]) {
      setOptions(cacheRef.current[q?q:"query"].options);
      return;
    }

    setIsLoading(true);
    makeAndHandleRequest(props.searchUri, q, lookupQuery, props.fieldNameForFilter, props.page, props.returnField, props.returnAllFields, props.globalSearch, perPage).then(
      (resp) => {
        const newCache = {
          ...cacheRef.current,
          [q?q:"query"]: { ...resp, page: props.page },
        };

        cacheRef.current = newCache;
        setIsLoading(false);
        setOptions(resp.options);
      }
    );
  }, [lookupQuery]);

  return (
    <AsyncTypeahead
      id="async-pagination-example"
      isLoading={isLoading}
      labelKey={props.labelKeyFunc?props.labelKeyFunc:(option) => `${option[props.returnField ?? (props.fieldNameForFilter || "name")]}`}
      maxResults={perPage - 1}
      minLength={props?.initSearch?0:2}
      onInputChange={handleInputChange}
      onPaginate={handlePagination}
      onSearch={handleSearch}
      onChange={(selected) => props.onSelected(selected)}
      options={options}
      paginate
      paginationText={"Load More"}
      searchText={"..."}
      placeholder={props.placeholder ?? ""}
      renderMenuItemChildren={(option) => (
        <div key={option.id}>
            <Highlighter
                highlightClassName={"srchHighlighter"}
                searchWords={query != "" ? [query] : []}
                autoEscape={true}
                // textToHighlight={titleCase(option[props.returnField ?? (props.fieldNameForFilter || "name")])}
                textToHighlight={props?.showLabelFunc?props.showLabelFunc(option):option[props.returnField ?? (props.fieldNameForFilter || "name")]}
            ></Highlighter>
        </div>
      )}
      useCache={false}
      highlightOnlyResult
      emptyLabel={props.emptyLabel}
      selected={props.selected || []}
      renderInput={({
        inputRef,
        referenceElementRef,
        ...inputProps
      }) => {
          return (
            <TextField
              {...inputProps}
              variant="outlined"
              aria-label="Search"
              ref={(input) => {
                  inputRef(input);
                  referenceElementRef(input);
              }}
              className={clsx(props?.classes?.tableFilterInput, props?.classes?.root)}
            />
          );
      }}
      disabled={props.disabled || false}
    />
  );
}
